import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import { Button, Field } from '@noorden-insurtech/alba-design-system';
import { FileImage, FileText, Upload, X } from 'lucide-react';
import { useRef, type DragEvent } from 'react';
import type { TFieldContext } from '../types';

export type UploadedFile = {
  name: string;
  size: number;
  file: File;
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
}

function getFileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext ?? '');
  return isImage ? 'image' : 'file';
}

export type UploadFieldProps = {
  label: string;
  required?: boolean;
  helperText?: string;
  disabled?: boolean;
  accept?: string;
  multiple?: boolean;
};

const DEFAULT_ACCEPT =
  'application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png';

export function createUploadField(useFieldContext: () => unknown) {
  return function UploadField({
    label,
    required,
    helperText,
    disabled,
    accept = DEFAULT_ACCEPT,
    multiple = true,
  }: UploadFieldProps) {
    const field = useFieldContext() as TFieldContext<UploadedFile[]>;
    const inputRef = useRef<HTMLInputElement>(null);

    const files: UploadedFile[] = field.state.value ?? [];

    const handleFiles = (incoming: FileList | null) => {
      if (!incoming) return;
      const newFiles: UploadedFile[] = Array.from(incoming).map(f => ({
        name: f.name,
        size: f.size,
        file: f,
      }));
      field.handleChange(multiple ? [...files, ...newFiles] : newFiles);
    };

    const handleRemove = (index: number) => {
      field.handleChange(files.filter((_, i) => i !== index));
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (disabled) return;
      handleFiles(e.dataTransfer.files);
    };

    return (
      <RequiredFieldTooltip active={required}>
      <div className='flex flex-col gap-2'>
        <Field.Root invalid={!!field.state.meta.errors.length}>
          <Field.Label>
            {label}
            {required && <span className='text-alba-error-red-500'>*</span>}
          </Field.Label>

          <div
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            className='flex flex-col items-start gap-2'
          >
            <Button
              variant='secondary'
              size='md'
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className='size-5' strokeWidth={1.6} />
              Adicionar arquivos
            </Button>

            <input
              ref={inputRef}
              type='file'
              accept={accept}
              multiple={multiple}
              disabled={disabled}
              className='hidden'
              onChange={e => handleFiles(e.target.files)}
            />
          </div>

          {helperText && !field.state.meta.errors.length && (
            <Field.HelperText>{helperText}</Field.HelperText>
          )}
          {field.state.meta.errors.length > 0 && (
            <Field.ErrorText>{field.state.meta.errors[0]}</Field.ErrorText>
          )}
        </Field.Root>

        {files.length > 0 && (
          <ul className='grid grid-cols-4 gap-[15px] mt-1'>
            {files.map((f, i) => {
              const isImage = getFileIcon(f.name) === 'image';
              const Icon = isImage ? FileImage : FileText;
              return (
                <li
                  key={`${f.name}-${i}`}
                  className='flex items-center gap-2 overflow-hidden rounded-xl border border-alba-gray-400 bg-white p-2'
                >
                  <Icon
                    className='size-6 shrink-0 text-alba-black-500'
                    strokeWidth={1.6}
                  />
                  <div className='flex min-w-0 flex-1 flex-col'>
                    <div className='flex items-center justify-between gap-1'>
                      <span className='truncate text-sm font-bold font-montserrat text-alba-black-500'>
                        {f.name}
                      </span>
                      <button
                        type='button'
                        onClick={() => handleRemove(i)}
                        disabled={disabled}
                        className='shrink-0 text-alba-black-500 hover:text-alba-error-red-500 transition-colors disabled:cursor-not-allowed'
                      >
                        <X className='size-4' strokeWidth={1.6} />
                      </button>
                    </div>
                    <span className='text-xs font-montserrat text-alba-gray-600'>
                      {formatFileSize(f.size)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      </RequiredFieldTooltip>
    );
  };
}
